import React, { useState, useCallback, useEffect } from "react";
import { Stack, Box, Typography } from "@mui/material";
import BadgeIcon from "@mui/icons-material/Badge";

import { DashboardHeader } from "../shared";
import TaskModal from "../Clinical/TaskModal";
import EmployeeStatCards from "./EmployeeStatCards";
import EmployeeTasksPanel from "./EmployeeTasksPanel";
import QuickActions from "./QuickActions";
import SuggestedObservations from "./SuggestedObservations";

import useTasks from "../../hooks/useTasks";
import useAuthUser from "../../../../hooks/useAuthUser";
import { getEmployees } from "../../../../utils/services/control/masterDataService";
import { TASK_CATEGORIES, TASK_PRIORITIES } from "../../constants/clinicalDashboard";

/**
 * EmployeeDashboard — Dashboard genérico para empleados sin perfil
 * ejecutivo, clínico ni de care management (recepción, administración,
 * back office, facturación, IT, invitados, etc.).
 *
 * No muestra datos de pacientes (PHI). Es un espacio de trabajo personal
 * centrado en la PRODUCTIVIDAD: tareas propias, observaciones/notas,
 * accesos rápidos y un resumen del estado de sus pendientes.
 *
 * Las tareas se filtran por `creatorId` (las creadas por el empleado),
 * reutilizando el mismo backend de tareas que los demás dashboards.
 */
export default function EmployeeDashboard() {
  const currentUser = useAuthUser();
  const employeeId = currentUser?.employeeId || currentUser?.employee || null;
  const displayName = currentUser?.name || currentUser?.fullName || "there";

  const {
    tasks,
    stats: taskStats,
    loading,
    createTask,
    completeTask,
    updateTask,
    deleteTask,
  } = useTasks(null, { creatorId: employeeId });

  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    let cancelled = false;
    getEmployees({ status: 1 })
      .then((data) => { if (!cancelled) setEmployees(data || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // ── Task Modal State ─────────────────────────────────────────
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [taskModalDefaults, setTaskModalDefaults] = useState(null);

  const openTaskModal = useCallback((defaults = null) => {
    setTaskModalDefaults(defaults);
    setTaskModalOpen(true);
  }, []);

  const handleCreateTask = useCallback(
    (taskData) => {
      const categoryId =
        TASK_CATEGORIES.find((c) => c.key === taskData.category)?.id || TASK_CATEGORIES[8]?.id;
      const priorityId =
        TASK_PRIORITIES.find((p) => p.key === taskData.priority)?.id || TASK_PRIORITIES[1]?.id;
      createTask({
        ...taskData,
        categoryId,
        priorityId,
        locationId: null,
        creatorId: employeeId,
      });
    },
    [createTask, employeeId]
  );

  const todayLabel = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <Stack spacing={2.5}>
      {/* Header */}
      <DashboardHeader
        title="My Workspace"
        subtitle="Your personal hub for tasks, observations, and daily notes"
        icon={<BadgeIcon />}
        color="info"
      />

      {/* Greeting */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={0.5}
      >
        <Typography variant="h6" fontWeight={700}>
          Welcome back, {displayName}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {todayLabel}
        </Typography>
      </Stack>

      {/* Stat cards */}
      <EmployeeStatCards stats={taskStats} loading={loading} />

      {/* Row: My Tasks (wide) + Quick Actions / Observations (side) */}
      <Box
        sx={{
          display: "grid",
          gap: 2.5,
          gridTemplateColumns: { xs: "1fr", lg: "1.6fr 1fr" },
          alignItems: "start",
        }}
      >
        <EmployeeTasksPanel
          tasks={tasks}
          loading={loading}
          onComplete={(id) => completeTask(id, employeeId)}
          onCancel={(id) => updateTask(id, 29, employeeId)}
          onDelete={(id) => deleteTask(id, employeeId)}
          onEdit={(task) => openTaskModal(task)}
          onNewTask={() => openTaskModal()}
        />

        <Stack spacing={2.5}>
          <QuickActions onQuickCreate={openTaskModal} />
          <SuggestedObservations onQuickCreate={openTaskModal} />
        </Stack>
      </Box>

      {/* Task Modal */}
      <TaskModal
        open={taskModalOpen}
        onClose={() => setTaskModalOpen(false)}
        onSubmit={handleCreateTask}
        defaultValues={taskModalDefaults}
        patients={[]}
        employees={employees}
        currentUser={currentUser}
      />
    </Stack>
  );
}
